import { useSyncExternalStore } from "react"
import { BREAKPOINTS } from "@/constants/ui"

import { selectViewSize, ViewSize } from "@/lib/storage/app-slice"
import { useAppSelector } from "@/lib/storage/hooks"

type UseMediaQueryOptions = {
  /**
   * The answer on the server and through hydration when the view size is not
   * known or the query is not about width.
   */
  defaultValue?: boolean
}

// The width a width query is answered at before the client can read the
// viewport. Every phone width answers alike; tablets are taken to be in
// portrait (md) and desktops laptop-sized (xl). Other widths get the right
// answer after hydration.
const ASSUMED_WIDTHS: Record<ViewSize, number> = {
  xs: BREAKPOINTS.sm - 1,
  sm: BREAKPOINTS.md,
  split: BREAKPOINTS.xl,
}

/** What a min-/max-width query says at `width`; undefined if it is not one. */
function matchesWidth(query: string, width: number): boolean | undefined {
  const bounds = [...query.matchAll(/\((min|max)-width:\s*(\d+)px\)/g)]
  if (!bounds.length) return undefined
  return bounds.every(([, side, px]) =>
    side === "min" ? width >= Number(px) : width <= Number(px)
  )
}

/**
 * Whether the viewport matches a media query. The server, and the client while
 * it hydrates server HTML, answer from the view size the server guessed, so
 * both render the same markup; the client's own answer follows hydration. A
 * client-only render (a soft navigation, a late mount) reads the viewport
 * straight away.
 */
export function useMediaQuery(
  query: string,
  { defaultValue = false }: UseMediaQueryOptions = {}
): boolean {
  const viewSize = useAppSelector(selectViewSize)
  const serverMatches =
    (viewSize && matchesWidth(query, ASSUMED_WIDTHS[viewSize])) ?? defaultValue

  return useSyncExternalStore(
    (onChange) => {
      const mediaQueryList = window.matchMedia(query)
      mediaQueryList.addEventListener("change", onChange)
      return () => mediaQueryList.removeEventListener("change", onChange)
    },
    () => window.matchMedia(query).matches,
    () => serverMatches
  )
}
